/**
 * Pure math for the mouse-driven parallax backdrop (see ./parallax.ts for the
 * Pixi shell). Kept free of pixi/DOM imports so it runs headless in unit tests.
 *
 * Model: the pointer pans a virtual camera. Layers shift against the pointer,
 * nearer (higher-z) layers by more, which is what sells the depth; all motion is
 * eased so the scene never snaps.
 */

/** A device tilt of this many degrees on an axis equals full deflection (a pointer at the viewport edge). */
const TILT_RANGE_DEG = 30;
/** Tilt below this magnitude (deg) reads as rest — kills the jitter of a hand that is never perfectly still. */
const TILT_DEADZONE_DEG = 1.5;
/** Horizontal pixels of travel per z at full pointer deflection. */
const PIXELS_PER_Z = 22;
/** Vertical travel is damped — screens are wide, and big y-shifts read as scrolling. */
const Y_RATIO = 0.6;
/** Easing rate for offsets (per second); higher = snappier camera. */
const APPROACH_RATE = 6;

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/** Map a client-space pointer to [-1, 1] on both axes (0,0 = viewport centre). */
export function normalisePointer(
  clientX: number,
  clientY: number,
  width: number,
  height: number,
): Vec2 {
  if (width <= 0 || height <= 0) {
    return { x: 0, y: 0 };
  }
  return {
    x: Math.min(1, Math.max(-1, (clientX / width) * 2 - 1)),
    y: Math.min(1, Math.max(-1, (clientY / height) * 2 - 1)),
  };
}

/**
 * Map a phone's tilt to the same [-1, 1] camera space `normalisePointer` produces,
 * so the gyroscope drives the parallax identically to the mouse (see the
 * DeviceOrientation guide: https://trekhleb.dev/blog/2021/gyro-web/).
 *
 * `gamma` is the left-right tilt (degrees) and drives the horizontal pan — the
 * primary mobile parallax gesture. `beta` is the front-back tilt and drives the
 * (damped) vertical pan; `referenceBeta` is the angle the phone rested at when
 * tracking began — people hold a phone tilted rather than flat, so that resting
 * pose becomes the neutral centre. A small deadzone kills hand jitter, and both
 * axes clamp so a hard tilt saturates rather than flinging the scene off-screen.
 */
export function tiltToPointer(beta: number, gamma: number, referenceBeta: number): Vec2 {
  const deadzone = (deg: number): number => (Math.abs(deg) < TILT_DEADZONE_DEG ? 0 : deg);
  const clamp = (v: number): number => Math.min(1, Math.max(-1, v));
  return {
    x: clamp(deadzone(gamma) / TILT_RANGE_DEG),
    y: clamp(deadzone(beta - referenceBeta) / TILT_RANGE_DEG),
  };
}

/**
 * Target offset for a layer at depth `z`: against the pointer (a camera pan),
 * scaling linearly with depth so separation between layers stays constant.
 */
export function layerOffset(pointer: Vec2, z: number): Vec2 {
  return {
    x: -pointer.x * PIXELS_PER_Z * z,
    y: -pointer.y * PIXELS_PER_Z * Y_RATIO * z,
  };
}

/**
 * Frame-rate-independent exponential easing: step `current` toward `target` by
 * the fraction `1 - e^(-rate·dt)`, so two 8 ms steps land exactly where one
 * 16 ms step would.
 */
export function approach(
  current: number,
  target: number,
  dtSeconds: number,
  rate: number = APPROACH_RATE,
): number {
  if (dtSeconds <= 0) {
    return current;
  }
  return current + (target - current) * (1 - Math.exp(-rate * dtSeconds));
}

/** Where a sprite layer sits and how big it is, as viewport fractions. */
export interface SpriteLayout {
  /** Horizontal anchor position, fraction of viewport width. */
  readonly fx: number;
  /** Vertical anchor position, fraction of viewport height. */
  readonly fy: number;
  /** Sprite height as a fraction of viewport height (nearer = larger). */
  readonly heightFrac: number;
}

const frac = (n: number): number => n - Math.floor(n);

/**
 * Deterministic scatter for sprite layers: golden-ratio strides keep any number
 * of layers spread out without clustering, no RNG involved (the same pack always
 * composes the same backdrop). Positions stay away from the extreme edges so an
 * eased offset never strands a sprite mostly off-screen.
 */
export function spriteLayout(index: number, z: number): SpriteLayout {
  return {
    fx: 0.12 + frac(0.23 + index * 0.618034) * 0.76,
    fy: 0.14 + frac(0.41 + index * 0.381966) * 0.62,
    heightFrac: 0.1 + z * 0.06,
  };
}

/**
 * Gentle autonomous bob so the backdrop is alive with the mouse still. Bounded
 * (±4 px x, ±3 px y), incommensurate frequencies per layer so nothing marches
 * in lockstep.
 */
export function ambientDrift(phaseSeconds: number, index: number): Vec2 {
  return {
    x: Math.sin(phaseSeconds * 0.21 + index * 2.4) * 4,
    y: Math.cos(phaseSeconds * 0.17 + index * 1.7) * 3,
  };
}
